import {
  patchAuthUserNickname,
  readPersistedGameNickname,
  type AuthUser,
} from '@core/gameengine/state/auth';

const SKYX_PLAYER_NICK_PATTERN = /^player-([a-z0-9]+)$/i;
const OWN_CHAT_SEQS_STORAGE_PREFIX = 'playgrid-own-chat-client-seqs:';
const MAX_PERSISTED_OWN_CHAT_SEQS = 200;

export function normalizeChatIdentity(
  value: string | null | undefined
): string {
  return value?.trim().toLowerCase() ?? '';
}

function extractSkyxPlayerNicknameSuffix(
  nickname: string | null | undefined
): string {
  const match = SKYX_PLAYER_NICK_PATTERN.exec(nickname?.trim() ?? '');
  return match?.[1]?.toLowerCase() ?? '';
}

export function doesSkyxNicknameMatchPlayerId(
  nickname: string | null | undefined,
  playerId: string | null | undefined
): boolean {
  const suffix = extractSkyxPlayerNicknameSuffix(nickname);
  const normalizedPlayerId = playerId?.trim().toLowerCase() ?? '';
  if (!suffix || !normalizedPlayerId) {
    return false;
  }
  return (
    normalizedPlayerId.endsWith(suffix) || normalizedPlayerId.includes(suffix)
  );
}

function readPersistedOwnChatClientSeqs(playerId: string): Set<string> {
  if (!playerId.trim()) {
    return new Set();
  }
  try {
    const raw = localStorage.getItem(
      `${OWN_CHAT_SEQS_STORAGE_PREFIX}${playerId}`
    );
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(
      parsed.filter((value): value is string => typeof value === 'string')
    );
  } catch {
    return new Set();
  }
}

function writePersistedOwnChatClientSeqs(
  playerId: string,
  clientSeqs: Set<string>
): void {
  if (!playerId.trim()) {
    return;
  }
  try {
    const values = Array.from(clientSeqs).slice(-MAX_PERSISTED_OWN_CHAT_SEQS);
    localStorage.setItem(
      `${OWN_CHAT_SEQS_STORAGE_PREFIX}${playerId}`,
      JSON.stringify(values)
    );
  } catch {
    // ignore
  }
}

export function rememberOwnChatClientIds(
  playerId: string | null | undefined,
  clientIds: Iterable<string>
): void {
  const trimmedPlayerId = playerId?.trim() ?? '';
  if (!trimmedPlayerId) {
    return;
  }

  const persisted = readPersistedOwnChatClientSeqs(trimmedPlayerId);
  let changed = false;
  for (const clientId of clientIds) {
    const trimmed = clientId.trim();
    if (trimmed && !persisted.has(trimmed)) {
      persisted.add(trimmed);
      changed = true;
    }
  }

  if (changed) {
    writePersistedOwnChatClientSeqs(trimmedPlayerId, persisted);
  }
}

export function collectChatClientIds(payload: {
  meta?: { clientSeq?: string; clientId?: string };
  content?: { clientSeq?: string; clientId?: string };
}): string[] {
  const ids = [
    payload.meta?.clientSeq,
    payload.meta?.clientId,
    payload.content?.clientSeq,
    payload.content?.clientId,
  ]
    .map(value => value?.trim() ?? '')
    .filter(Boolean);

  return [...new Set(ids)];
}

function collectOwnChatIdentities(user: AuthUser | null): Set<string> {
  const identities = new Set<string>();
  const add = (value: string | null | undefined) => {
    const normalized = normalizeChatIdentity(value);
    if (normalized) {
      identities.add(normalized);
    }
  };

  add(user?.nickname);
  add(user?.username);
  add(user?.playerId);
  add(readPersistedGameNickname());

  return identities;
}

function hasSentOrPersistedClientId(
  clientIds: string[],
  options: {
    user: AuthUser | null;
    sentClientSeqs?: ReadonlySet<string>;
  }
): boolean {
  if (clientIds.length === 0) {
    return false;
  }

  const persisted = options.user?.playerId
    ? readPersistedOwnChatClientSeqs(options.user.playerId)
    : new Set<string>();

  return clientIds.some(
    clientId => options.sentClientSeqs?.has(clientId) || persisted.has(clientId)
  );
}

export function isOwnChatMessage(
  message: { user: string; clientSeq: string },
  options: {
    user: AuthUser | null;
    sentClientSeqs?: ReadonlySet<string>;
  }
): boolean {
  const clientIds = message.clientSeq?.trim() ? [message.clientSeq.trim()] : [];
  if (hasSentOrPersistedClientId(clientIds, options)) {
    return true;
  }

  const messageUser = message.user?.trim() ?? '';
  if (!messageUser) {
    return false;
  }

  if (doesSkyxNicknameMatchPlayerId(messageUser, options.user?.playerId)) {
    return true;
  }

  return collectOwnChatIdentities(options.user).has(
    normalizeChatIdentity(messageUser)
  );
}

export function isOwnChatPayload(
  payload: {
    user?: {
      nickName?: string;
      nickname?: string;
      playerId?: string;
      id?: string;
    };
    meta?: { clientSeq?: string; clientId?: string };
    content?: { clientSeq?: string; clientId?: string };
  },
  options: {
    user: AuthUser | null;
    sentClientSeqs?: ReadonlySet<string>;
  }
): boolean {
  const clientIds = collectChatClientIds(payload);
  if (hasSentOrPersistedClientId(clientIds, options)) {
    return true;
  }

  const messageUser =
    payload.user?.nickName ??
    payload.user?.nickname ??
    payload.user?.playerId ??
    payload.user?.id ??
    '';

  return isOwnChatMessage({ user: messageUser, clientSeq: '' }, options);
}

export function trackOwnChatPayload(
  payload: {
    user?: { nickName?: string; nickname?: string };
    meta?: { clientSeq?: string; clientId?: string };
    content?: { clientSeq?: string; clientId?: string };
  },
  options: {
    user: AuthUser | null;
    sentClientSeqs: Set<string>;
    persistNickname?: boolean;
  }
): boolean {
  const isOwn = isOwnChatPayload(payload, options);
  if (!isOwn) {
    return false;
  }

  const clientIds = collectChatClientIds(payload);
  for (const clientId of clientIds) {
    options.sentClientSeqs.add(clientId);
  }
  rememberOwnChatClientIds(options.user?.playerId, clientIds);

  if (options.persistNickname !== false) {
    const nick =
      payload.user?.nickName?.trim() || payload.user?.nickname?.trim();
    if (nick) {
      patchAuthUserNickname(nick);
    }
  }

  return true;
}

export function trackOwnChatSend(
  clientId: string,
  user: AuthUser | null,
  sentClientSeqs: Set<string>
): void {
  const trimmed = clientId.trim();
  if (!trimmed) {
    return;
  }

  sentClientSeqs.add(trimmed);
  rememberOwnChatClientIds(user?.playerId, [trimmed]);

  const nick =
    user?.nickname?.trim() ||
    readPersistedGameNickname() ||
    user?.username?.trim();
  if (nick && SKYX_PLAYER_NICK_PATTERN.test(nick)) {
    patchAuthUserNickname(nick);
  }
}
